// One full game tick: movement plus game rules (bhop blocks, checkpoints, zones/timer, fail height).
// Order matches the prototype's tick(); see docs/MOVEMENT.md.

import { BhopBlock, BlockContact, QUICK_FAIL_TICKS } from './bhopBlocks';
import type { CollisionWorld } from './collision/types';
import { DT, EYE_STAND, FAIL_Z, PRESPEED_CAP, defaultCvars, type Cvars } from './constants';
import { IN_MOVELEFT, IN_MOVERIGHT, type UserCmd } from './input';
import { noclipMove, playerMove } from './movement';
import { createPlayer, horizontalSpeed, type PlayerState, type SpawnPoint } from './player';
import { RunStats } from './stats';
import { Weapons } from './weapons';
import { RunTimer } from './timer';
import { copy3, type Vec3 } from './vec';
import { inZone, type Zone } from './zones';

/** Everything the simulation needs to know about a map. */
export interface Level {
  world: CollisionWorld;
  blocks: BhopBlock[];
  spawn: SpawnPoint;
  startZone: Zone | null;
  endZone: Zone | null;
  stageCount: number;
  /** Falling below this height respawns the player. Defaults to FAIL_Z (the built-in course's pit). */
  failZ?: number;
  /** Zones that split the run into stages, in order (loaded maps). */
  checkpointZones?: Zone[];
  /**
   * Respawn point of each stage zone (box levels). Entering zone i then sets the respawn and the
   * stage, run or not, so jumping over a checkpoint platform still counts.
   */
  stageSpawns?: SpawnPoint[];
  /** Map entities: movers, triggers, teleports, map I/O (loaded maps). */
  runtime?: LevelRuntime;
}

/** Per-tick game logic of a loaded map's entities. Must be deterministic. */
export type Surface = 'concrete' | 'metal' | 'wood' | 'glass';

export interface LevelRuntime {
  /** Back to the map's start state (called on restart). */
  reset(sim: Simulation): void;
  /** A bullet hit this solid (map entities may break). Returns true if something broke. */
  damageSolid?(solid: number, amount: number): boolean;
  /** Before player movement: move doors/platforms, carry the player standing on them. */
  beforeMove(sim: Simulation): void;
  /** After player movement: bhop block rule, triggers, teleports, map I/O. */
  afterMove(sim: Simulation): void;
  /** What a weapon hit is made of, for impact sounds (not used by the simulation). */
  surface?(solid: number, pos: Readonly<Vec3>): Surface;
  /** Chat messages from the map ("say" commands), shown and cleared by the client. */
  readonly messages?: string[];
}

// Event bits, set during tick(), read and cleared by the client.
export const EV_FINISHED = 1;
/** The finish screen should go away (back in the start zone, or restarted). */
export const EV_HIDE_FINISH = 2;
export const EV_TELEPORTED = 4;
export const EV_JUMPED = 8;
/** Landed this tick; speed in player.landSpeed. */
export const EV_LANDED = 16;
/** The knife hit something solid (what: weapons.knife). */
export const EV_KNIFE_HIT = 32;
/** Something broke (glass). */
export const EV_BROKE = 64;
/** Entered a new stage during a run (split in `splits`). */
export const EV_STAGE = 128;

export class Simulation {
  readonly cv: Cvars = defaultCvars();
  autobhop = true;
  readonly player: PlayerState = createPlayer();
  readonly contact = new BlockContact();
  readonly timer = new RunTimer();
  readonly stats = new RunStats();
  readonly weapons = new Weapons();
  /** Ticks since the simulation was created (seeds weapon spread). */
  tickCount = 0;
  /** Current respawn point (start or last checkpoint). */
  spawn: SpawnPoint;
  stage = 1;
  /** Run time (ticks) when each stage after the first was reached, this run. */
  readonly splits: number[] = [];
  events = 0;
  /** The command of the tick being run (for map entities that react to attacks). */
  cmd: Readonly<UserCmd> | null = null;
  /** The timer only starts after the player has been in the start zone since the last restart. */
  armed = false;
  /**
   * Bhop blocks sink a little and vanish, and the player is reset to the checkpoint right away
   * (default). Off: the prototype's rule, where the block and the player fall into the pit.
   */
  quickBlockFail = true;
  /** Ticks until the player is reset after the block under them dropped (0: none pending). */
  private blockFail = 0;

  constructor(readonly level: Level) {
    this.spawn = level.spawn;
    this.restart();
    this.events = 0;
  }

  /** Re-sync the yaw used for strafe sync, e.g. after the mouse was released. */
  resyncYaw(yaw: number) {
    this.player.lastYaw = yaw;
  }

  teleport(sp: SpawnPoint) {
    const p = this.player;
    copy3(p.pos, sp.pos);
    copy3(p.prev, sp.pos);
    p.vel[0] = p.vel[1] = p.vel[2] = 0;
    p.baseVel[0] = p.baseVel[1] = p.baseVel[2] = 0;
    p.ground = null;
    p.ladder = false;
    p.sliding = false;
    p.ducked = false;
    p.fx.eyeSnap = EYE_STAND;
    p.fx.eyeShift = 0;
    p.fx.snapYaw = sp.yaw;
    p.lastYaw = sp.yaw;
    this.blockFail = 0;
    this.stats.speedHist.clear();
    this.contact.clear();
    for (const b of this.level.blocks) b.reset();
    this.events |= EV_TELEPORTED;
  }

  /**
   * Map teleport (trigger_teleport): move the player, zero velocity, face `yaw`. Unlike teleport(),
   * keeps duck state and doesn't reset blocks or the run.
   */
  teleportTo(pos: Readonly<Vec3>, yaw: number | null) {
    const p = this.player;
    copy3(p.pos, pos);
    copy3(p.prev, pos);
    p.vel[0] = p.vel[1] = p.vel[2] = 0;
    p.baseVel[0] = p.baseVel[1] = p.baseVel[2] = 0;
    p.ground = null;
    p.ladder = false;
    p.sliding = false;
    if (yaw !== null) {
      p.fx.snapYaw = yaw;
      p.lastYaw = yaw;
    }
    this.contact.clear();
    this.events |= EV_TELEPORTED;
  }

  restart() {
    this.timerOff = false;
    this.spawn = this.level.spawn;
    this.stage = 1;
    this.splits.length = 0;
    this.armed = false;
    this.teleport(this.spawn);
    this.timer.reset();
    this.stats.reset();
    this.weapons.refill();
    this.player.laggedMovement = 1;
    this.player.lagAcc = 0;
    this.player.gravity = 1;
    this.level.runtime?.reset(this);
    this.events |= EV_HIDE_FINISH;
  }

  /** Testing aid (remove before release): fly freely, no collision, triggers, fails or timer. */
  noclip = false;

  /**
   * No timer until the next restart (after teleporting to a stage or flying with noclip, the run
   * doesn't count; walking back into the start zone doesn't start one either).
   */
  timerOff = false;

  setNoclip(on: boolean) {
    if (on === this.noclip) return;
    this.noclip = on;
    const p = this.player;
    p.vel[0] = p.vel[1] = p.vel[2] = 0;
    p.baseVel[0] = p.baseVel[1] = p.baseVel[2] = 0;
    p.ground = null;
    p.ladder = false;
    p.sliding = false;
    // Flying around ends the run; go back through the start zone for a new one.
    this.armed = false;
    this.timer.reset();
    this.events |= EV_HIDE_FINISH;
  }

  tick(cmd: Readonly<UserCmd>) {
    const p = this.player;
    const level = this.level;
    const stats = this.stats;
    const timer = this.timer;
    this.cmd = cmd;
    copy3(p.prev, p.pos);

    const blocks = level.blocks;
    const quick = this.quickBlockFail;
    for (let i = 0; i < blocks.length; i++) blocks[i].update(p, quick);
    level.runtime?.beforeMove(this);

    const yawDelta = cmd.yaw - p.lastYaw;
    p.lastYaw = cmd.yaw;

    const wasGround = p.ground !== null;
    const groundBefore = p.ground;
    if (this.noclip) noclipMove(p, cmd);
    else {
      // player_speedmod: more (or fewer) moves per tick; exactly one normally.
      let moves = 1;
      if (p.laggedMovement !== 1) {
        p.lagAcc += p.laggedMovement;
        moves = Math.floor(p.lagAcc);
        p.lagAcc -= moves;
      }
      for (let i = 0; i < moves; i++)
        if (playerMove(p, cmd, level.world, this.cv, this.autobhop)) {
          this.events |= EV_JUMPED;
          stats.jumps++;
          stats.jumpSpeeds.push(Math.round(p.jumpSpeed));
          // Booster pad: jumping off it launches you.
          const boost = groundBefore?.boost;
          if (boost) {
            p.vel[0] += boost[0];
            p.vel[1] += boost[1];
            p.vel[2] += boost[2];
          }
        }
    }

    if (!wasGround && p.ground) this.events |= EV_LANDED;

    this.contact.update(p.ground ? p.ground.block : null);
    // The block under the player just dropped: reset them shortly, while it sinks out of view.
    const dropped = this.contact.block;
    if (quick && dropped && dropped.state === 'falling' && this.blockFail === 0 && !this.noclip)
      this.blockFail = QUICK_FAIL_TICKS;

    const cp = p.ground ? p.ground.checkpoint : undefined;
    if (cp && this.spawn !== cp) {
      this.spawn = cp;
      this.stage = cp.stage;
    }

    this.tickCount++;
    this.weapons.tick(cmd, p, level.world, this.tickCount);
    if (this.weapons.knife.hit) this.events |= EV_KNIFE_HIT;
    const shot = this.weapons.shot;
    if (
      shot.fired &&
      shot.hit &&
      shot.solid > 0 &&
      level.runtime?.damageSolid?.(shot.solid, this.weapons.damage)
    )
      this.events |= EV_BROKE;

    level.runtime?.afterMove(this);

    // Strafe sync: turning left while holding only A, or right while holding only D.
    if (!p.ground && Math.abs(yawDelta) > 1e-7) {
      stats.syncTotal++;
      const left = (cmd.buttons & IN_MOVELEFT) !== 0;
      const right = (cmd.buttons & IN_MOVERIGHT) !== 0;
      if ((yawDelta > 0 && left && !right) || (yawDelta < 0 && right && !left)) stats.syncGood++;
    }

    // Zones and timer. Levels without a start zone have no timer.
    if (!level.startZone || this.noclip || this.timerOff) {
      // Free play.
    } else if (inZone(level.startZone, p.pos)) {
      this.armed = true;
      if (level.checkpointZones) {
        this.stage = 1;
        this.splits.length = 0;
        if (level.stageSpawns) this.spawn = level.spawn;
      }
      if (timer.state !== 'zone') this.events |= EV_HIDE_FINISH;
      timer.state = 'zone';
      timer.ticks = 0;
      stats.reset();
      const hs = horizontalSpeed(p);
      if (hs > PRESPEED_CAP) {
        const r = PRESPEED_CAP / hs;
        p.vel[0] *= r;
        p.vel[1] *= r;
      }
    } else if (timer.state === 'zone' && this.armed) {
      timer.state = 'run';
      timer.ticks = 0;
    }
    const stageSpawns = level.stageSpawns;
    if (stageSpawns && level.checkpointZones && !this.noclip) {
      // Box levels: any later stage zone you are in becomes your stage and respawn point.
      const cps = level.checkpointZones;
      for (let i = cps.length - 1; i >= this.stage - 1; i--) {
        if (!inZone(cps[i], p.pos)) continue;
        const reached = i + 2;
        this.spawn = stageSpawns[i];
        if (timer.state === 'run') while (this.splits.length < reached - 1) this.splits.push(timer.ticks + 1);
        if (timer.state === 'run') this.events |= EV_STAGE;
        this.stage = reached;
        break;
      }
    }
    if (timer.state === 'run') {
      timer.ticks++;
      const cps = level.checkpointZones;
      if (!stageSpawns && cps && this.stage <= cps.length && inZone(cps[this.stage - 1], p.pos)) {
        this.stage++;
        this.splits.push(timer.ticks);
        this.events |= EV_STAGE;
      }
      if (level.endZone && inZone(level.endZone, p.pos)) {
        timer.state = 'done';
        timer.finished = timer.ticks * DT;
        this.events |= EV_FINISHED;
      }
    }

    if (this.blockFail > 0 && --this.blockFail === 0) this.teleport(this.spawn);
    else if (!this.noclip && p.pos[2] < (level.failZ ?? FAIL_Z)) this.teleport(this.spawn);

    stats.speedHist.push(horizontalSpeed(p));
  }
}
